import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Loader } from "semantic-ui-react";

import { Can, Tabs, useSelectedLocation } from "components";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import {
  DeskCheckIn,
  DiagnoseOverview,
  General,
  Kiosk,
  OnlineCheckIn,
  RepairOverview,
  ServiceTracker,
  Support,
  Variables
} from "modules/CommunicationSettings/components";
import "modules/CommunicationSettings/CustomerCom.scss";
import { useCommunicationSettingsMutation, useCommunicationSettingsQuery, usePdfTemplatesQuery } from "modules/CommunicationSettings/hooks";
import { useDMSv3DealersListQuery } from "modules/DMSv3/Settings/components/DMSv3SettingsTable/hooks";

import type { TabData } from "components";
import type { CommunicationSetting } from "models";

const FormHelper = createFormHelper<CommunicationSetting>();

export const CommunicationSettings = () => {
  const { t } = useTranslation();

  const { selectedLocation } = useSelectedLocation();
  const { data: dmsDealersList = [] } = useDMSv3DealersListQuery();
  const { data: communicationSettings, isLoading: isLoadingCommunicationSettings } = useCommunicationSettingsQuery();
  const { data: pdfTemplates, isLoading: isLoadingPDFTemplates } = usePdfTemplatesQuery();
  const { mutate: updateCommunicationSettings, isPending: isUpdatingCommunicationSettings } = useCommunicationSettingsMutation();

  const dms = selectedLocation?.online_check_in_enabled
    ? dmsDealersList.find(dms => dms.dealer_id === selectedLocation.dealer_id && dms.dms_id === selectedLocation.dms_id)
    : undefined;

  const onSubmitHandler = (formValues: CommunicationSetting) => {
    if (dms && formValues.check_in_days_prior >= dms.date_range) return;

    updateCommunicationSettings(formValues);
  };

  const tabsData: Map<string, TabData> = useMemo(
    () =>
      new Map([
        ["general", { data: [], id: "general", title: t("v8_general", "General") }],
        ["variables", { data: [], id: "variables", title: t("v8_variables", "Variables") }],
        ["onlineCheckIn", { data: [], id: "onlineCheckIn", title: t("v8_online_check_in", "Online Check in") }],
        ["deskCheckIn", { data: [], id: "deskCheckIn", title: t("v8_desk_check_in", "Desk Check in") }],
        ["diagnoseOverview", { data: [], id: "diagnoseOverview", title: t("v8_diagnose_overview", "Diagnose Overview") }],
        ["repairOverview", { data: [], id: "repairOverview", title: t("v8_repair_overview", "Repair Overview") }],
        ["kiosk", { data: [], id: "kiosk", title: t("v8_kiosk", "Kiosk") }],
        ["serviceTracker", { data: [], id: "serviceTracker", title: t("v8_service_tracker", "Service Tracker") }],
        ["support", { data: [], id: "support", title: t("v8_support", "Support") }]
      ]),
    []
  );

  if (isLoadingCommunicationSettings || isLoadingPDFTemplates) return <Loader />;

  const templatesDropdownOptions =
    pdfTemplates?.map(item => ({
      key: item.id,
      value: item.id,
      text: item.name
    })) ?? [];

  return (
    <FormHelper.Form
      onSubmit={formValues =>
        onSubmitHandler({
          ...formValues,
          star_rating_experience_message: formValues.star_rating_experience_message || t("v8_how_easy_was_experience", "How easy was the experience?"),
          star_rating_service_message: formValues.star_rating_service_message || t("v8_rate_customer_service", "How do you rate our customer service?"),
          star_rating_speed_message: formValues.star_rating_speed_message || t("v8_rate_speed_of_process", "How do you rate the speed of the process?")
        })
      }
      defaultValues={communicationSettings}
      formProps={{ loading: isLoadingCommunicationSettings, disabled: isUpdatingCommunicationSettings }}
    >
      <div className="customer-com-container">
        <div className="customer-com-header">
          <div className="row center">
            <div className="full customer-com-tabs">
              <Tabs data={tabsData} />
            </div>
            <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.CommunicationSettings}>
              <div className="save">
                <Button color="green">
                  {t("v8_save", "Save")}
                  <Icon className="check" />
                </Button>
              </div>
            </Can>
          </div>
        </div>
        <General tabId={tabsData.get("general")?.id as string} />
        <Variables tabId={tabsData.get("variables")?.id as string} />
        <OnlineCheckIn tabId={tabsData.get("onlineCheckIn")?.id as string} dmsDateRange={dms?.date_range} />
        <DeskCheckIn tabId={tabsData.get("deskCheckIn")?.id as string} />
        <DiagnoseOverview tabId={tabsData.get("diagnoseOverview")?.id as string} />
        <RepairOverview tabId={tabsData.get("repairOverview")?.id as string} pdfTemplateOptions={templatesDropdownOptions} />
        <Kiosk tabId={tabsData.get("kiosk")?.id as string} />
        <ServiceTracker tabId={tabsData.get("serviceTracker")?.id as string} />
        <Support tabId={tabsData.get("support")?.id as string} />
      </div>
    </FormHelper.Form>
  );
};
